/**
 * CreatorsDominanceCard - Top creators by GMV with visitor gating
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logRequest } from '@/hooks/useRequestLogger';
import { Crown, ArrowRight, Lock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useRole } from '@/contexts/RoleContext';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { formatMetricCount } from '@/components/arsenal/CreatorCard';
import { formatGmvFromCents } from '@/lib/formatCurrency';
import { CreatorAvatarImage } from '@/components/arsenal/CreatorAvatarImage';

interface TopCreator {
  id: string;
  nickname: string | null;
  username: string;
  avatar_url: string | null;
  source_avatar_url: string | null;
  gmv_cents: number;
  units_sold: number | null;
  follower_count: number | null;
  engagement_rate: number | null;
  country: string | null;
}

const DISPLAY_COUNT = 8;

async function fetchTopCreators(hourIndex: number): Promise<{ creators: TopCreator[]; totalGmvCents: number; totalCount: number }> {
  const { data, error } = await supabase
    .from('arsenal_creators')
    .select('id, nickname, username, avatar_url, source_avatar_url, gmv_cents, units_sold, follower_count, engagement_rate, country')
    .gt('follower_count', 0)
    .gt('heart_count', 0)
    .gt('video_count', 0)
    .order('gmv_cents', { ascending: false });

  if (error) return { creators: [], totalGmvCents: 0, totalCount: 0 };
  logRequest('arsenal_creators');

  const all = (data ?? []) as TopCreator[];
  const totalGmvCents = all.reduce((sum, c) => sum + (c.gmv_cents ?? 0), 0);

  // Rotate selection based on hour
  let creators: TopCreator[];
  if (all.length <= DISPLAY_COUNT) {
    creators = all;
  } else {
    const offset = hourIndex % all.length;
    const doubled = [...all, ...all];
    creators = doubled.slice(offset, offset + DISPLAY_COUNT);
  }

  return { creators, totalGmvCents, totalCount: all.length };
}

function CreatorsDominanceCardInner() {
  const { effectiveRole } = useRole();
  const { redirectToPV } = usePVRedirect();
  const isVisitor = effectiveRole === 'visitor';
  const hourIndex = Math.floor(Date.now() / 3_600_000);

  const { data, isLoading } = useQuery({
    queryKey: ['creators-dominance', hourIndex],
    queryFn: () => fetchTopCreators(hourIndex),
    staleTime: 5 * 60 * 1000,
  });

  const creators = data?.creators ?? [];
  const totalGmv = data?.totalGmvCents ?? 0;
  const totalCount = data?.totalCount ?? 0;

  if (!isLoading && creators.length === 0) return null;

  return (
    <div className="forja-card rounded-xl p-3 md:p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
            <Crown className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-sm md:text-base font-bold text-foreground">
              Criadores que dominam o TikTok Shop
            </h2>
            <p className="text-[9px] md:text-[10px] text-muted-foreground">
              {isVisitor
                ? `${totalCount} criadores com faturamento milionário monitorados`
                : `${totalCount} criadores monitorados`}
            </p>
          </div>
        </div>
      </div>

      {/* Creators horizontal scroll */}
      {isLoading ? (
        <div className="flex gap-3 overflow-hidden mb-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="w-[100px] h-[120px] rounded-lg flex-shrink-0" />
          ))}
        </div>
      ) : (
        <div className="relative mb-3">
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
            {creators.map((creator) => (
              <div
                key={creator.id}
                className="relative w-[80px] md:w-[100px] aspect-[9/16] rounded-lg overflow-hidden flex-shrink-0 border border-border/30 bg-secondary/50 cursor-pointer group/thumb"
                onClick={() => !isVisitor && window.location.assign('/arsenal?tab=criadores')}
              >
                {/* Avatar with full fallback chain: CDN → bucket → initials */}
                <CreatorAvatarImage
                  creator={creator}
                  size="full"
                  fallback="initial"
                  loading="eager"
                  imgClassName={cn(isVisitor && 'blur-[4px]')}
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Info overlay at bottom */}
                <div className={cn(
                  "absolute bottom-0 left-0 right-0 p-1.5 flex flex-col gap-0.5",
                  isVisitor && "blur-[3px] select-none"
                )}>
                  <span className="text-[10px] md:text-xs font-bold text-white truncate leading-tight">
                    {creator.nickname || creator.username}
                  </span>
                  <span className="text-[8px] md:text-[9px] text-white/60 truncate">
                    @{creator.username}
                  </span>
                  <span className="text-[10px] md:text-xs font-bold text-primary">
                    {formatGmvFromCents(creator.gmv_cents, creator.country === 'US' ? 'USD' : 'BRL')}
                  </span>
                  {creator.units_sold && creator.units_sold > 0 && (
                    <span className="text-[8px] text-emerald-300/80">
                      {formatMetricCount(creator.units_sold)} vendidos
                    </span>
                  )}
                </div>

                {/* Visitor lock */}
                {isVisitor && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Lock className="w-3.5 h-3.5 text-white/70" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Fade edge */}
          <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card/80 to-transparent pointer-events-none" />
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto">
      {isVisitor ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full gap-1.5 text-xs"
          onClick={() => redirectToPV('creators-card')}
        >
          Quero ser membro
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Link to="/arsenal?tab=criadores" className="block">
          <Button size="sm" variant="ghost" className="w-full gap-1.5 text-xs">
            Ver todos os criadores
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </Link>
      )}
      </div>
    </div>
  );
}

const CreatorsDominanceCard = React.memo(CreatorsDominanceCardInner);
export default CreatorsDominanceCard;
